<template>
  <div id="vote">
    <div class="arrows">
      <span v-if="nominee" class="nominee" :style="nomineeStyle" />
      <span v-if="nominator" class="nominator" :style="nominatorStyle" />
    </div>
    <div class="overlay">
      <em class="blue">{{ nominatorDisplayName }}</em>
      {{ voteAction }}
      <em v-if="!shouldHideNominee">
        {{ nomineeDisplayName }}
      </em>
      {{ t('vote.exclam') }}
      <br>
      <em v-if="
        !grimoire.isSecretVote ||
        (nominee && nominee.role?.team === 'traveler') ||
        !session.isPlayerOrSpectator
      " class="blue">
        {{ votesCount }} {{ t('vote.votes') }}
      </em>
      <em v-else class="blue"> ? {{ t('vote.votes') }} </em>
      {{ t('vote.inFavor') }}
      <em v-if="
        (nominee && nominee.role?.team !== 'traveler') ||
        (votingStore.nomination && typeof votingStore.nomination.nominee === 'string')
      ">
        ({{ t('vote.majorityIs') }} {{ Math.ceil(alive / 2) }})
      </em>
      <em v-else-if="nominee">
        ({{ t('vote.majorityIs') }} {{ Math.ceil(players.length / 2) }})
      </em>

      <template v-if="!session.isPlayerOrSpectator">
        <br>
        <em v-if="
          grimoire.isSecretVote &&
          ((nominee && nominee.role?.team !== 'traveler') ||
            (votingStore.nomination && typeof votingStore.nomination.nominee === 'string'))
        " class="orange">
          {{ t('vote.secretBallot') }}
        </em>
        <div
          v-if="(!votingStore.isVoteInProgress && votingStore.lockedVote < 1) || votingStore.lockedVote === players.length + 1">
          {{ t('vote.timePerPlayer') }}
          <font-awesome-icon icon="minus-circle" class="fa fa-minus-circle" @mousedown.prevent="setVotingSpeed(-250)" />
          {{ votingStore.votingSpeed / 1000 }}s
          <font-awesome-icon icon="plus-circle" class="fa fa-plus-circle" @mousedown.prevent="setVotingSpeed(250)" />
        </div>
        <div class="button-group">
          <div v-if="!votingStore.isVoteInProgress" class="button townsfolk" @click="countdown">
            {{ t('vote.countdown') }}
          </div>
          <div v-if="!votingStore.isVoteInProgress" class="button" @click="start()">
            {{ votingStore.lockedVote ? t('vote.restart') : t('vote.start') }}
          </div>
          <template v-else>
            <div class="button townsfolk" :class="{ disabled: !votingStore.lockedVote }" @click="pause()">
              {{ isVoteTimerRunning() ? t('vote.pause') : t('vote.resume') }}
            </div>
            <div class="button" @click="stop">
              {{ t('vote.reset') }}
            </div>
          </template>
          <div class="button demon" @click="finish">
            {{ t('vote.close') }}
          </div>
        </div>
        <div v-if="
          !shouldHideNominee &&
          (!nominee || nominee.role?.team !== 'traveler')
        " class="button-group mark">
          <div class="button" :class="{
            disabled: votingStore.nomination?.nominee === votingStore.markedPlayer,
          }" @click="setMarked">
            {{ t('vote.setMarked') }}
          </div>
          <div class="button" @click="removeMarked">
            {{ t('vote.removeMarked') }}
          </div>
        </div>
      </template>
      <template v-else-if="canVote">
        <div v-if="!votingStore.isVoteInProgress">
          {{ votingStore.votingSpeed / 1000 }} {{ t('vote.secondsBetweenVotes') }}
        </div>
        <div class="button-group">
          <div class="button townsfolk" :class="{ disabled: !currentVote }" @click="vote(0)">
            {{ t('vote.handDown') }}
          </div>
          <div class="button demon" :class="{ disabled: currentVote === 1 }" @click="vote(1)">
            {{ t('vote.handUp') }}
          </div>
          <div
            v-if="player?.canVoteTwice && ((nominee && nominee.role?.team !== 'traveler') || (votingStore.nomination && typeof votingStore.nomination.nominee === 'string'))"
            class="button demon" :class="{ disabled: currentVote === 2 }" @click="vote(2)">
            x2
          </div>
        </div>
      </template>
      <div v-if="session.sessionId && session.isPlayerOrSpectator && !session.isSeated">
        {{ t('vote.seatToVote') }}
      </div>
      <Countdown v-if="grimoire.timer.duration || grimoire.timer.paused" :timer-name="grimoire.timer.name"
        :timer-duration="grimoire.timer.duration" :paused="grimoire.timer.paused ?? false" />
      <Jukebox />
    </div>
    <div v-if="votingStore.isVoteInProgress && !votingStore.lockedVote" class="countdown">
      <span>3</span>
      <span>2</span>
      <span>1</span>
      <span>{{ t('vote.doVote') }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted } from "vue";
import { Countdown, Jukebox } from '@/components';
import { isActiveNomination } from '@/services';
import {
  useChatStore,
  useGrimoireStore,
  useLocaleStore,
  usePlayersStore,
  useSessionStore,
  useVotingStore,
  useSoundboardStore,
  useDialogStore,
} from "@/stores";
import type { Player } from '@/types';
import { useTimer } from '@/composables/useTimer';

const clockStartVolume = 0.3;
const clockEndVolume = 0.5;

const chatStore = useChatStore();
const grimoire = useGrimoireStore();
const locale = useLocaleStore();
const playersStore = usePlayersStore();
const session = useSessionStore();
const votingStore = useVotingStore();
const dialogStore = useDialogStore();
const t = locale.t;

let timerForRiser: ReturnType<typeof setTimeout>;

const alive = computed(() => playersStore.alive);
const players = computed(() => playersStore.players);

const { startInterval, stopInterval, isRunning: isVoteTimerRunning } = useTimer();

const nominator = computed(() => {
  if (!isActiveNomination(votingStore.nomination)) return null;

  const nominatorRef = votingStore.nomination.nominator;
  if (typeof nominatorRef === 'number') {
    return players.value[nominatorRef] || null;
  }
  return null; // For special votes with string/null nominators
});

const nominatorStyle = computed(() => {
  if (!isActiveNomination(votingStore.nomination)) return {};

  const playersCount = players.value.length;
  const nominatorRef = votingStore.nomination.nominator;

  if (typeof nominatorRef !== 'number') return {};

  if (nominee.value) {
    return {
      transform: `rotate(${Math.round((nominatorRef / playersCount) * 360)}deg)`,
      transitionDuration: votingStore.votingSpeed - 100 + "ms",
    };
  } else {
    const lock = votingStore.lockedVote;
    const rotation =
      (360 * (nominatorRef + Math.min(lock, playersCount))) / playersCount;
    return {
      transform: `rotate(${Math.round(rotation)}deg)`,
      transitionDuration: votingStore.votingSpeed - 100 + "ms",
    };
  }
});

const nominee = computed(() => {
  if (!isActiveNomination(votingStore.nomination)) return null;

  const nomineeRef = votingStore.nomination.nominee;
  if (typeof nomineeRef === 'number') {
    return players.value[nomineeRef] || null;
  }
  return null; // For special votes with string/null nominees
});

const nomineeStyle = computed(() => {
  if (!isActiveNomination(votingStore.nomination)) return {};

  const playersCount = players.value.length;
  const nomineeRef = votingStore.nomination.nominee;

  if (typeof nomineeRef !== 'number') return {};

  const lock = votingStore.lockedVote;
  const rotation = (360 * (nomineeRef + Math.min(lock, playersCount))) / playersCount;
  return {
    transform: `rotate(${Math.round(rotation)}deg)`,
    transitionDuration: votingStore.votingSpeed - 100 + "ms",
  };
});

const isFreeVote = computed(() => {
  if (!isActiveNomination(votingStore.nomination)) return false;
  return (
    votingStore.nomination.nominee === null
    || nominee.value?.role?.team === 'traveler'
  )
});

const player = computed(() => {
  return playersStore.getById(session.playerId)
});

const currentVote = computed(() => {
  const index = players.value.findIndex((p: Player) => p.id === session.playerId);
  return index >= 0 ? votingStore.votes[index] : undefined;
});

// Type-safe computed properties for nomination handling
const nominatorDisplayName = computed(() => {
  if (!isActiveNomination(votingStore.nomination)) return '';

  if (nominator.value) {
    return nominator.value.name;
  }

  const nominatorRef = votingStore.nomination.nominator;
  if (typeof nominatorRef === 'string') {
    return nominatorRef.charAt(0).toUpperCase() + nominatorRef.slice(1);
  }

  return '';
});

const nomineeDisplayName = computed(() => {
  if (!isActiveNomination(votingStore.nomination)) return '';

  if (nominee.value) {
    return nominee.value.name;
  }

  const nomineeRef = votingStore.nomination.nominee;
  if (typeof nomineeRef === 'string') {
    return nomineeRef;
  }

  return '';
});

const voteAction = computed(() => {
  if (!isActiveNomination(votingStore.nomination)) return '';

  const nomination = votingStore.nomination;

  // Check if it's a special vote with custom action text
  if (nomination.specialVote?.timerText) {
    return nomination.specialVote.timerText;
  }

  // Check if it's a traveler exile
  if (nominee.value?.role?.team === 'traveler') {
    return t('vote.callexile');
  }

  // Default nomination action
  return t('vote.nominates');
});

const shouldHideNominee = computed(() => {
  if (!isActiveNomination(votingStore.nomination)) return false;

  const nomination = votingStore.nomination;

  // Hide nominee if it's a special vote with custom timer text (like cultleader, custom)
  // but show nominee for special votes like bishop/atheist that have string nominees
  return !!nomination.specialVote?.timerText;
});

const canVote = computed(() => {
  if (!player.value || !isActiveNomination(votingStore.nomination)) return false;

  const nomination = votingStore.nomination;

  if ( // Dead player without a token or voudon
    (player.value.isDead || playersStore.hasFeature(player.value.role, "need-token"))
    && !player.value.voteToken
    && !isFreeVote.value
    && !playersStore.isFeatureInPlay("free-dead-vote")
  ) return false;

  const playersCount = players.value.length;
  const index = players.value.indexOf(player.value);

  // Determine reference player index for vote order calculation
  let referenceIndex: number;
  if (nominee.value && typeof nomination.nominee === 'number') {
    referenceIndex = nomination.nominee;
  } else if (typeof nomination.nominator === 'number') {
    referenceIndex = nomination.nominator;
  } else {
    referenceIndex = 0;
  }

  const indexAdjusted =
    (index - 1 + playersCount - referenceIndex) % playersCount;
  return indexAdjusted >= votingStore.lockedVote - 1;
});

const votesCount = computed(() => {
  let res = 0;

  if (votingStore.lockedVote) {
    /* If we are already counting the votes, we only take account of the locked votes.
     * But, on locked votes, we also take account of vote modifiers like Bureaucrat or Thief (for the nomination of a non-Traveller).
     */

    if (votingStore.lockedVote === 1) {
      return 0; // We are just starting, no vote is counted yet.
    }

    let startClock = (typeof votingStore.nomination?.nominee === "number") ? votingStore.nomination?.nominee : votingStore.nomination?.nominator as number;
    let lastVote = startClock + votingStore.lockedVote - 1;
    if (lastVote >= playersStore.players.length) {
      lastVote -= playersStore.players.length;
    }

    if (lastVote > startClock) {
      for (let i = startClock + 1; i <= lastVote; i++) {
        if ((nominee.value && nominee.value.role?.team !== 'traveler') || (votingStore.nomination && typeof votingStore.nomination.nominee === 'string')) {
          res += playersStore.players[i]!.raisedHands * playersStore.players[i]!.voteValue;
        }
        else {
          res += playersStore.players[i]!.raisedHands;
        }
      }
    }
    else {
      for (let i = 0; i <= lastVote; i++) {
        if ((nominee.value && nominee.value.role?.team !== 'traveler') || (votingStore.nomination && typeof votingStore.nomination.nominee === 'string')) {
          res += playersStore.players[i]!.raisedHands * playersStore.players[i]!.voteValue;
        }
        else {
          res += playersStore.players[i]!.raisedHands;
        }
      }
      for (let i = startClock + 1; i < playersStore.players.length; i++) {
        if ((nominee.value && nominee.value.role?.team !== 'traveler') || (votingStore.nomination && typeof votingStore.nomination.nominee === 'string')) {
          res += playersStore.players[i]!.raisedHands * playersStore.players[i]!.voteValue;
        }
        else {
          res += playersStore.players[i]!.raisedHands;
        }
      }
    }
  }

  else {
    /*  If we are not counting the votes, then we take account of all the votes.
     *  We do not take account of modifiers like Bureaucrat or Thief.
     */
    for (const element of playersStore.players) {
      res += element!.raisedHands;
    }
  }

  return res;
});

const checkSeated = async () => {
  const unseated = playersStore.players.filter(p => p.id === "");
  if (unseated.length === 0) return true
  return await dialogStore.confirm(t('prompt.unseatedVote') + unseated.map(p => p.name).join(", "))
}

const countdown = async () => {
  if (!(await checkSeated())) return;
  const soundboard = useSoundboardStore();
  soundboard.playSound({ sound: "ringing" });
  votingStore.lockVote(0);
  votingStore.setVoteInProgress(true);
  startInterval(4000, () => {
    start(true);
  });
};

const start = async (areSeatsChecked: boolean = false) => {
  if (!areSeatsChecked && !(await checkSeated())) return
  const soundboard = useSoundboardStore();
  votingStore.lockVote(1);
  votingStore.setVoteInProgress(true);
  stopInterval();
  if (players.value.length * votingStore.votingSpeed >= 5000) {
    timerForRiser = setTimeout(() => {
      soundboard.playSound({ sound: "riser" });
    }, players.value.length * votingStore.votingSpeed - 5000);
  }
  startInterval(votingStore.votingSpeed, () => {
    votingStore.lockVote();
    if (votingStore.lockedVote > players.value.length) {
      if (isVoteTimerRunning()) {
        soundboard.playSound({ sound: "votingBell" });
        stopInterval();
      }
      votingStore.setVoteInProgress(false);
    }
    else if (votingStore.votingSpeed >= 500) {
      soundboard.changeVolume({ sound: "votingClock" }, clockStartVolume + (clockEndVolume - clockStartVolume) * Math.min((votingStore.lockedVote + 1) / players.value.length, 1));
      soundboard.playSound({ sound: "votingClock" });
    }
  });
};

const pause = () => {
  if (!checkSeated()) return
  const soundboard = useSoundboardStore();
  if (isVoteTimerRunning()) {
    stopInterval();
    clearTimeout(timerForRiser);
  } else {
    if ((players.value.length - votingStore.lockedVote) * votingStore.votingSpeed >= 5000) {
      timerForRiser = setTimeout(() => {
        soundboard.playSound({ sound: "riser" });
      }, (players.value.length) * votingStore.votingSpeed - 5000);
    }
    startInterval(votingStore.votingSpeed, () => {
      votingStore.lockVote();
      if (votingStore.lockedVote > players.value.length) {
        if (isVoteTimerRunning()) {
          soundboard.playSound({ sound: "votingBell" });
          stopInterval();
        }
        votingStore.setVoteInProgress(false);
      }
      else if (votingStore.votingSpeed >= 500) {
        soundboard.changeVolume({ sound: "votingClock" }, 0.2 + 0.7 * Math.min((votingStore.lockedVote + 1) / players.value.length, 1));
        soundboard.playSound({ sound: "votingClock" });
      }
    });
  }
};

const stop = () => {
  stopInterval();
  votingStore.setVoteInProgress(false);
  votingStore.lockVote(0);
};

const finish = () => {
  stopInterval();
  if (!session.isPlayerOrSpectator && votingStore.lockedVote > players.value.length) {
    chatStore.logEvent("chat.log.votes", {
      count: votesCount.value,
      players: players.value
        .filter((player: Player) => player.raisedHands)
        .map(({ name }: Player) => name)
        .join(", "),
    });
  }
  votingStore.addHistory({
    day: grimoire.dayCount,
    players: players.value,
    votes: votesCount.value,
    isSecretVoteMode: grimoire.isSecretVote,
    localeTexts: {
      exile: t('modal.voteHistory.exile'),
      execution: t('modal.voteHistory.execution')
    }
  });
  votingStore.setNomination(null);
};

const vote = (vote: number): boolean => {
  if (!canVote.value) return false;
  const index = players.value.findIndex((p: Player) => p.id === session.playerId);
  if (index >= 0 && votingStore.votes[index] !== vote) {
    votingStore.voteSync([index, vote]);
    playersStore.update({
      player: players.value.at(index) as Player,
      property: "raisedHands",
      value: vote
    })
    return true;
  }
  return false;
};

const toggleVote = (event: KeyboardEvent) => {
  if (!canVote.value) return;
  if (event.key !== " ") return;
  const target = event.target as HTMLElement;
  if (['INPUT', 'TEXTAREA'].includes(target.tagName) || target.isContentEditable) return;
  const index = players.value.findIndex((p: Player) => p.id === session.playerId);
  vote((votingStore.votes[index] === 0) ? 1 : 0);
}

const setVotingSpeed = (diff: number) => {
  const speed = Math.round(votingStore.votingSpeed + diff);
  if (speed >= 0) {
    votingStore.setVotingSpeed(speed);
  }
};

const setMarked = () => {
  votingStore.setMarkedPlayer(votingStore.nomination?.nominee as number);
};

const removeMarked = () => {
  votingStore.setMarkedPlayer(-1);
};

onMounted(() => {
  globalThis.addEventListener('keyup', toggleVote);
})

onUnmounted(() => {
  stopInterval();
  globalThis.removeEventListener('keyup', toggleVote);
});
</script>

<style lang="scss" src="./Vote.scss" scoped></style>
